import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { X, Check, Clock, ArrowRight, Workflow, Film } from "lucide-react";
import { Button } from "@/components/ui/button";

export const ProductInfoModal = ({ product: p, onClose, onRequest }) => {
    const { t } = useTranslation();

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    if (!p) return null;

    return (
        <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md flex items-start lg:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="product-info-modal-overlay">
            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className="bg-white rounded-md max-w-3xl w-full my-8 overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="product-info-modal"
            >
                <div className="relative h-48 md:h-64 bg-zinc-900">
                    {p.thumbnail_url && p.thumbnail_type === "video" ? (
                        <video src={p.thumbnail_url} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
                    ) : p.thumbnail_url ? (
                        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${p.thumbnail_url})` }} />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-950" />
                    )}
                    <button onClick={onClose} className="absolute top-4 right-4 h-9 w-9 flex items-center justify-center rounded-sm bg-black/50 text-white hover:bg-black/80" data-testid="product-info-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="px-6 lg:px-8 pt-6">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{p.name}</span>
                        {p.ai_badge && <span className="bg-blue-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm flex-shrink-0">AI</span>}
                        {p.featured && <span className="bg-[#E5A93D] text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm flex-shrink-0">{t("landing.packages.premium")}</span>}
                    </div>
                    <h2 className="font-display font-black text-2xl md:text-3xl text-zinc-950 tracking-tight">{p.tagline}</h2>
                </div>

                <div className="p-6 lg:p-8 space-y-6">
                    {p.description && <p className="text-sm text-zinc-600 leading-relaxed">{p.description}</p>}

                    {p.workflow && (
                        <div className="bg-[#FAF7F0] border border-[#E5A93D]/30 rounded-sm p-5" data-testid="product-info-workflow">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C8922C] font-bold mb-2">
                                <Workflow className="h-3.5 w-3.5" /> {t("landing.packages.workflow_title")}
                            </div>
                            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">{p.workflow}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        {(p.deliverables || []).map((d, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-zinc-600">
                                <Check className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                <span>{d}</span>
                            </div>
                        ))}
                        {p.duration && (
                            <div className="flex items-start gap-2 text-sm text-zinc-500">
                                <Check className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                <span>{p.duration}</span>
                            </div>
                        )}
                        {p.delivery_time && (
                            <div className="flex items-start gap-2 text-sm text-zinc-500">
                                <Clock className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                <span>{p.delivery_time}</span>
                            </div>
                        )}
                    </div>

                    {(p.example_videos || []).length > 0 && (
                        <div data-testid="product-info-videos">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">
                                <Film className="h-3.5 w-3.5" /> {t("landing.packages.example_videos")}
                            </div>
                            <div className={`grid gap-3 ${p.example_videos.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
                                {p.example_videos.map((url, i) => (
                                    <video key={i} src={url} controls playsInline preload="metadata" className="w-full rounded-sm border border-zinc-200 bg-black aspect-video object-contain" />
                                ))}
                            </div>
                        </div>
                    )}

                    {p.options && p.options.length > 0 && (
                        <div className="border-t border-zinc-100 pt-4">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("landing.packages.extra_options")}</div>
                            {p.options.map((o) => (
                                <div key={o.id} className="flex justify-between text-xs text-zinc-500 py-1">
                                    <span>{o.label}</span>
                                    <span className="text-zinc-600">{`+€${o.price}`}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex items-end justify-between pt-4 border-t border-zinc-100">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.price_from ? t("landing.packages.price_from_label") : t("landing.packages.price_label")}</div>
                            <div className="font-display font-black text-3xl text-[#E5A93D]">
                                {p.price_from && <span className="text-base font-medium text-zinc-500 mr-1">{t("landing.packages.from")}</span>}€{p.price}
                            </div>
                        </div>
                        <Button onClick={onRequest} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="product-info-request-btn">
                            {t("landing.packages.request")} <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default ProductInfoModal;
